import React, { useState, useEffect, createContext, useContext } from 'react';

// Breakpoint configuration
export const breakpoints = {
  xs: 0,
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
} as const;

export type Breakpoint = keyof typeof breakpoints;

// Responsive configuration
export interface ResponsiveConfig {
  // Layout settings
  containerMaxWidth: {
    sm: string;
    md: string;
    lg: string;
    xl: string;
    '2xl': string;
  };
  
  // Typography scaling
  typography: {
    baseSize: {
      mobile: string;
      tablet: string;
      desktop: string;
    };
    headingScale: {
      mobile: number;
      tablet: number;
      desktop: number;
    };
  };
  
  // Spacing system
  spacing: {
    mobile: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    desktop: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
  };
  
  // Touch targets
  touchTargets: {
    minimum: string;
    comfortable: string;
  };
}

export const defaultResponsiveConfig: ResponsiveConfig = {
  containerMaxWidth: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  typography: {
    baseSize: {
      mobile: '16px',
      tablet: '17px',
      desktop: '18px',
    },
    headingScale: {
      mobile: 1.2,
      tablet: 1.25,
      desktop: 1.3,
    },
  },
  
  spacing: {
    mobile: {
      xs: '0.5rem',
      sm: '1rem',
      md: '1.5rem',
      lg: '2rem',
      xl: '3rem',
    },
    desktop: {
      xs: '0.75rem',
      sm: '1.5rem',
      md: '2rem',
      lg: '3rem',
      xl: '4rem',
    },
  },
  
  touchTargets: {
    minimum: '44px',
    comfortable: '48px',
  },
};

// Responsive context
const ResponsiveContext = createContext<{
  breakpoint: Breakpoint;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  config: ResponsiveConfig;
}>({
  breakpoint: 'lg',
  isMobile: false,
  isTablet: false,
  isDesktop: true,
  config: defaultResponsiveConfig,
});

// Responsive provider
export const ResponsiveProvider: React.FC<{
  children: React.ReactNode;
  config?: Partial<ResponsiveConfig>;
}> = ({ children, config = {} }) => {
  const [breakpoint, setBreakpoint] = useState<Breakpoint>('lg');
  const mergedConfig = { ...defaultResponsiveConfig, ...config };

  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth;
      
      if (width >= breakpoints['2xl']) setBreakpoint('2xl');
      else if (width >= breakpoints.xl) setBreakpoint('xl');
      else if (width >= breakpoints.lg) setBreakpoint('lg');
      else if (width >= breakpoints.md) setBreakpoint('md');
      else if (width >= breakpoints.sm) setBreakpoint('sm');
      else setBreakpoint('xs');
    };

    updateBreakpoint();
    window.addEventListener('resize', updateBreakpoint);
    
    return () => window.removeEventListener('resize', updateBreakpoint);
  }, []);

  const isMobile = breakpoint === 'xs' || breakpoint === 'sm';
  const isTablet = breakpoint === 'md';
  const isDesktop = breakpoint === 'lg' || breakpoint === 'xl' || breakpoint === '2xl';

  return (
    <ResponsiveContext.Provider value={{
      breakpoint,
      isMobile,
      isTablet,
      isDesktop,
      config: mergedConfig,
    }}>
      {children}
    </ResponsiveContext.Provider>
  );
};

// Hook to use responsive context
export const useResponsive = () => {
  const context = useContext(ResponsiveContext);
  if (!context) {
    throw new Error('useResponsive must be used within ResponsiveProvider');
  }
  return context;
};

// Hook for media queries
export const useMediaQuery = (query: string): boolean => {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setMatches(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [query]);

  return matches;
};

// Responsive container component
export const ResponsiveContainer: React.FC<{
  children: React.ReactNode;
  className?: string;
  maxWidth?: Breakpoint;
}> = ({ children, className = '', maxWidth = '2xl' }) => {
  const { config } = useResponsive();
  
  return (
    <div 
      className={`mx-auto px-4 sm:px-6 lg:px-8 ${className}`}
      style={{ maxWidth: config.containerMaxWidth[maxWidth] }}
    >
      {children}
    </div>
  );
};

// Responsive grid component
export const ResponsiveGrid: React.FC<{
  children: React.ReactNode;
  cols?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
    '2xl'?: number;
  };
  gap?: string;
  className?: string;
}> = ({ 
  children, 
  cols = { xs: 1, sm: 2, md: 3, lg: 4 },
  gap = '1.5rem',
  className = '' 
}) => {
  const gridClasses = Object.entries(cols)
    .map(([breakpoint, colCount]) => {
      if (breakpoint === 'xs') return `grid-cols-${colCount}`;
      return `${breakpoint}:grid-cols-${colCount}`;
    })
    .join(' ');

  return (
    <div 
      className={`grid ${gridClasses} ${className}`}
      style={{ gap }}
    >
      {children}
    </div>
  );
};

// Responsive text component
export const ResponsiveText: React.FC<{
  children: React.ReactNode;
  size?: 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  className?: string;
}> = ({ children, size = 'base', className = '' }) => {
  const sizeClasses = {
    xs: 'text-xs sm:text-sm',
    sm: 'text-sm sm:text-base',
    base: 'text-base sm:text-lg',
    lg: 'text-lg sm:text-xl',
    xl: 'text-xl sm:text-2xl',
    '2xl': 'text-2xl sm:text-3xl',
    '3xl': 'text-3xl sm:text-4xl',
    '4xl': 'text-4xl sm:text-5xl lg:text-6xl',
  };

  return (
    <span className={`${sizeClasses[size]} ${className}`}>
      {children}
    </span>
  );
};

// Mobile-first responsive image
export const ResponsiveImage: React.FC<{
  src: string;
  alt: string;
  sizes?: string;
  className?: string;
  priority?: boolean;
}> = ({ 
  src, 
  alt, 
  sizes = '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw',
  className = '',
  priority = false 
}) => {
  return (
    <img
      src={src}
      alt={alt}
      sizes={sizes}
      className={`w-full h-auto ${className}`}
      loading={priority ? 'eager' : 'lazy'}
      decoding="async"
    />
  );
};

// Touch-friendly button
export const TouchButton: React.FC<{
  children: React.ReactNode;
  onClick: () => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  disabled?: boolean;
}> = ({ 
  children, 
  onClick, 
  size = 'md',
  className = '',
  disabled = false 
}) => {
  const { config } = useResponsive();
  
  const sizeClasses = {
    sm: 'px-3 py-2 text-sm',
    md: 'px-4 py-3 text-base',
    lg: 'px-6 py-4 text-lg',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`
        inline-flex items-center justify-center rounded-lg font-medium
        transition-all duration-200 focus:outline-none focus:ring-2 
        focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900
        ${sizeClasses[size]}
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 active:scale-95'}
        ${className}
      `}
      style={{ 
        minHeight: config.touchTargets.comfortable,
        minWidth: config.touchTargets.comfortable 
      }}
    >
      {children}
    </button>
  );
};

// Responsive spacing utility
export const getResponsiveSpacing = (
  size: keyof ResponsiveConfig['spacing']['mobile'],
  config: ResponsiveConfig
) => {
  return {
    mobile: config.spacing.mobile[size],
    desktop: config.spacing.desktop[size],
  };
};

// Responsive typography utility
export const getResponsiveTypography = (
  level: number,
  config: ResponsiveConfig
) => {
  const mobileSize = Math.pow(config.typography.headingScale.mobile, 6 - level);
  const tabletSize = Math.pow(config.typography.headingScale.tablet, 6 - level);
  const desktopSize = Math.pow(config.typography.headingScale.desktop, 6 - level);
  
  return {
    mobile: `${mobileSize}rem`,
    tablet: `${tabletSize}rem`,
    desktop: `${desktopSize}rem`,
  };
};

// Viewport utilities
export const getViewportWidth = (): number => {
  return typeof window !== 'undefined' ? window.innerWidth : 1024;
};

export const getViewportHeight = (): number => {
  return typeof window !== 'undefined' ? window.innerHeight : 768;
};

export const isLandscape = (): boolean => {
  return getViewportWidth() > getViewportHeight();
};

export const isPortrait = (): boolean => {
  return getViewportWidth() <= getViewportHeight();
};

// Device detection utilities
export const isTouchDevice = (): boolean => {
  return typeof window !== 'undefined' && 'ontouchstart' in window;
};

export const isIOS = (): boolean => {
  return typeof window !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
};

export const isAndroid = (): boolean => {
  return typeof window !== 'undefined' && /Android/.test(navigator.userAgent);
};

export default {
  ResponsiveProvider,
  ResponsiveContainer,
  ResponsiveGrid,
  ResponsiveText,
  ResponsiveImage,
  TouchButton,
  useResponsive,
  useMediaQuery,
  breakpoints,
  defaultResponsiveConfig,
};